// Curated list of country dial codes ordered for the Indian legal market:
// India first (default), neighbouring South Asia, then APAC, GCC/UK/US, then
// the long tail alphabetically. Flag emoji is decorative only — the parsed
// value sent to the backend is the raw dial code, e.g. "+91".
export const DIAL_CODES = [
    // ── Default + South Asia ──
    { code: "IN", dial: "+91",  name: "India",         flag: "🇮🇳" },
    { code: "BD", dial: "+880", name: "Bangladesh",    flag: "🇧🇩" },
    { code: "BT", dial: "+975", name: "Bhutan",        flag: "🇧🇹" },
    { code: "LK", dial: "+94",  name: "Sri Lanka",     flag: "🇱🇰" },
    { code: "NP", dial: "+977", name: "Nepal",         flag: "🇳🇵" },
    { code: "PK", dial: "+92",  name: "Pakistan",      flag: "🇵🇰" },
    { code: "MV", dial: "+960", name: "Maldives",      flag: "🇲🇻" },
    // ── GCC / Middle East (large NRI advocate base) ──
    { code: "AE", dial: "+971", name: "UAE",           flag: "🇦🇪" },
    { code: "SA", dial: "+966", name: "Saudi Arabia",  flag: "🇸🇦" },
    { code: "QA", dial: "+974", name: "Qatar",         flag: "🇶🇦" },
    { code: "KW", dial: "+965", name: "Kuwait",        flag: "🇰🇼" },
    { code: "OM", dial: "+968", name: "Oman",          flag: "🇴🇲" },
    { code: "BH", dial: "+973", name: "Bahrain",       flag: "🇧🇭" },
    // ── Anglo / common diaspora ──
    { code: "US", dial: "+1",   name: "United States", flag: "🇺🇸" },
    { code: "CA", dial: "+1",   name: "Canada",        flag: "🇨🇦" },
    { code: "GB", dial: "+44",  name: "United Kingdom",flag: "🇬🇧" },
    { code: "AU", dial: "+61",  name: "Australia",     flag: "🇦🇺" },
    { code: "NZ", dial: "+64",  name: "New Zealand",   flag: "🇳🇿" },
    // ── APAC ──
    { code: "SG", dial: "+65",  name: "Singapore",     flag: "🇸🇬" },
    { code: "MY", dial: "+60",  name: "Malaysia",      flag: "🇲🇾" },
    { code: "HK", dial: "+852", name: "Hong Kong",     flag: "🇭🇰" },
    { code: "JP", dial: "+81",  name: "Japan",         flag: "🇯🇵" },
    { code: "CN", dial: "+86",  name: "China",         flag: "🇨🇳" },
    { code: "TH", dial: "+66",  name: "Thailand",      flag: "🇹🇭" },
    { code: "ID", dial: "+62",  name: "Indonesia",     flag: "🇮🇩" },
    { code: "PH", dial: "+63",  name: "Philippines",   flag: "🇵🇭" },
    { code: "VN", dial: "+84",  name: "Vietnam",       flag: "🇻🇳" },
    // ── Europe ──
    { code: "DE", dial: "+49",  name: "Germany",       flag: "🇩🇪" },
    { code: "FR", dial: "+33",  name: "France",        flag: "🇫🇷" },
    { code: "NL", dial: "+31",  name: "Netherlands",   flag: "🇳🇱" },
    { code: "IT", dial: "+39",  name: "Italy",         flag: "🇮🇹" },
    { code: "ES", dial: "+34",  name: "Spain",         flag: "🇪🇸" },
    { code: "CH", dial: "+41",  name: "Switzerland",   flag: "🇨🇭" },
    { code: "SE", dial: "+46",  name: "Sweden",        flag: "🇸🇪" },
    { code: "IE", dial: "+353", name: "Ireland",       flag: "🇮🇪" },
    // ── Other ──
    { code: "ZA", dial: "+27",  name: "South Africa",  flag: "🇿🇦" },
    { code: "BR", dial: "+55",  name: "Brazil",        flag: "🇧🇷" },
    { code: "RU", dial: "+7",   name: "Russia",        flag: "🇷🇺" },
    { code: "TR", dial: "+90",  name: "Turkey",        flag: "🇹🇷" },
    { code: "MX", dial: "+52",  name: "Mexico",        flag: "🇲🇽" },
];

export const DEFAULT_DIAL = "+91";

// Sorted by dial-code length DESC so the longest prefix wins when parsing
// (e.g. "+971..." matches UAE before "+9...").
const DIALS_SORTED = [...DIAL_CODES].sort((a, b) => b.dial.length - a.dial.length);

/** Parse a stored E.164-ish string into { dial, national } parts.
 *  - "+919876543210"  → { dial: "+91",  national: "9876543210" }
 *  - "+1 415 555 1212" → { dial: "+1",   national: "4155551212" }
 *  - "9876543210"     → { dial: "+91",  national: "9876543210" }   (assumes IN)
 *  - "" / null         → { dial: "+91",  national: "" }
 */
export function parsePhone(raw) {
    if (!raw) return { dial: DEFAULT_DIAL, national: "" };
    const cleaned = String(raw).trim().replace(/[\s\-()]/g, "");
    if (cleaned.startsWith("+")) {
        for (const c of DIALS_SORTED) {
            if (cleaned.startsWith(c.dial)) {
                return { dial: c.dial, national: cleaned.slice(c.dial.length) };
            }
        }
        // Unknown country code — split heuristically (1-3 chars)
        const m = cleaned.match(/^(\+\d{1,3})(\d*)$/);
        if (m) return { dial: m[1], national: m[2] };
    }
    // Legacy bare digits — assume India for backward compatibility
    return { dial: DEFAULT_DIAL, national: cleaned.replace(/^0+/, "") };
}

/** Format dial + national → canonical E.164 ("+91XXXXXXXXXX") for storage. */
export function formatPhone(dial, national) {
    const d = (dial || DEFAULT_DIAL).startsWith("+") ? dial : `+${dial}`;
    const n = String(national || "").replace(/\D/g, "");
    if (!n) return "";
    return `${d}${n}`;
}
